import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { routesPath } from '@router/routes';
import { replacePathParams } from '@utils/replacePathWithParams';
import { ReadingsTable } from './ReadingsTable';
import { WorkstationResultsTable } from './WorkstationResultsTable';
import { useDXAReadingsSlice } from '@restore/DXAReadings/DXAReadings.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { GroupedLabels } from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { filterDXALabels, addedLabelsToVisits } from 'src/utils/dxaHelpers';
import { DXAWorkstationDataDTO } from '@dynamika-shared/types/DXAReading';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { WorkstationPageHeader } from '../components/WorkstationPageHeader';
import { prepareUrl } from 'src/utils/routerHelpers';

type DXAPageViewProps = {
  loading?: boolean;
  clinicalTrialId: string;
  taskId: string;
};
export const DXAPageView: FC<DXAPageViewProps> = ({
  loading,
  clinicalTrialId,
  taskId,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [
    {
      lastSync,
      loadingReadings,
      loadingWorkstationReadings,
      syncStatus,
      visitData,
      workstationData,
    },
    {
      syncWorkstation,
      fetchDXAReadings,
      fetchDXAWorkstationReadings,
      getSyncStatus,
      sendToReadings,
      removeSelectedReading,
    },
  ] = useDXAReadingsSlice((state, actions) => [state, actions]);

  const [assigneeId, taskStatus, taskDataIsLoading] = useQCTaskSlice(state => [
    state.data?.assigneeId,
    state.data?.status,
    state.loading,
  ]);

  const goToTaskInfo = () =>
    navigate(prepareUrl(routesPath.qcTaskInfo, [clinicalTrialId, taskId]));

  const groupedLabels = useClinicalTrialContextSlice(
    s => (s.groupedLabels as GroupedLabels).DXA ?? []
  );

  const filteredGroupLabels = useMemo(() => {
    const noRepeatLabels = filterDXALabels(groupedLabels, visitData);

    return noRepeatLabels;
  }, [groupedLabels, visitData]);

  const visitDataWithAssignedLabelName = useMemo(() => {
    const updatedLabels = addedLabelsToVisits(visitData, groupedLabels);

    return {
      ...visitData,
      dxaScores: updatedLabels,
    } as DXAWorkstationDataDTO | null;
  }, [visitData, groupedLabels]);

  useEffect(() => {
    fetchDXAReadings({ clinicalTrialId, taskId });
    fetchDXAWorkstationReadings({ clinicalTrialId, taskId });
    getSyncStatus({ clinicalTrialId, taskId });
  }, [clinicalTrialId, taskId]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('previousLocation', pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid='QCTask'
      className={clsx('min-h-[calc(100%-3.6rem)] pb-10')}
    >
      <div className='flex items-center justify-between py-4'>
        <WorkstationPageHeader
          title='DXA QC'
          goToTaskInfo={goToTaskInfo}
          assigneeExist={
            !!assigneeId && taskStatus !== 'DONE' && taskStatus !== 'FAILED'
          }
          loading={
            loading ||
            loadingWorkstationReadings ||
            loadingReadings ||
            taskDataIsLoading
          }
        />
        {assigneeId && taskStatus !== 'DONE' && taskStatus !== 'FAILED' && (
          <Button
            className='min-w-[12rem] rounded-lg ml-1 !px-2'
            type='primary'
            onClick={() => {
              navigate(
                replacePathParams(routesPath.qcTaskInfo, {
                  id: clinicalTrialId,
                  taskId,
                })
              );
            }}
          >
            Save & Close
          </Button>
        )}
      </div>
      <div
        data-testid='WorkstationResults'
        className='overflow-auto h-[calc(100vh-250px)] px-2'
      >
        <div className=''>
          <ReadingsTable
            visitData={visitDataWithAssignedLabelName}
            onRemove={data =>
              removeSelectedReading({
                clinicalTrialId,
                taskId,
                data,
              })
            }
            loading={loading || loadingReadings || taskDataIsLoading}
            readOnly={
              !assigneeId || taskStatus === 'DONE' || taskStatus === 'FAILED'
            }
          />
        </div>
        <div className=''>
          <WorkstationResultsTable
            labels={filteredGroupLabels}
            syncStatus={syncStatus}
            sendToReadings={data =>
              sendToReadings({
                clinicalTrialId,
                taskId,
                data,
              })
            }
            onSync={() => syncWorkstation({ clinicalTrialId, taskId })}
            lastSync={lastSync}
            workstationData={workstationData}
            loading={
              loading ||
              loadingWorkstationReadings ||
              loadingReadings ||
              taskDataIsLoading
            }
            readOnly={
              !assigneeId || taskStatus === 'DONE' || taskStatus === 'FAILED'
            }
          />
        </div>
      </div>
    </div>
  );
};
